import { Formik } from 'formik';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import * as actions from '../../../redux/actions';
import * as selectors from '../../../redux/selectors';
import * as _ from 'lodash';
import PageContainer from '../../common/PageContainer';
import { useEffect, useState } from 'react';
import { buildOptionsFromStrings } from '../../../utils/buildOptionsFromArray';
import { Breadcrumb, Button, Card, Icon, Loader, Menu, Form, Segment } from 'semantic-ui-react';
import ReactTable from '../../common/ReactTable';
import { isEmpty, path, pipe } from 'ramda';
import AddSeriesModal from './AddSeriesModal';

export const DEFAULT_SERIES_QUERY = {
  site: 'All',
  title: '',
  type: 'All',
  sponsor: '',
  time: 'All Time',
  active: false,
};

export const isBlankSeriesFilter = value =>
  value === undefined || value === null || value === '' || value === 'All';

const normalize = value => (value || '').toString().trim().toUpperCase();

const getSearchText = rec =>
  [
    rec?.data?.name,
    rec?.data?.headline,
    rec?.data?.title,
    rec?.title,
    rec?.url,
    rec?.slug,
    rec?.full_url,
    rec?.data?.sponsor,
    rec?.data?.sponsorName,
    rec?.data?.sponsor_name,
  ]
    .map(normalize)
    .join(' ');

export const getSeriesStartDate = (time, now = moment()) => {
  switch (time) {
    case 'Today':
      return now.clone().startOf('day');
    case 'Last 7 Days':
      return now.clone().subtract(7, 'days').startOf('day');
    case 'Last 30 Days':
      return now.clone().subtract(30, 'days').startOf('day');
    case 'This Year':
      return now.clone().startOf('year');
    default:
      return now.clone().subtract(10000, 'days').startOf('day');
  }
};

export const isSeriesActive = (record, now = moment()) => {
  const startDate = record?.data?.startDate;
  const endDate = record?.data?.endDate;
  if (!startDate || !endDate) return false;

  const today = now.clone().startOf('day');
  return (
    today.isSameOrAfter(moment(startDate, 'LL').startOf('day')) &&
    today.isSameOrBefore(moment(endDate, 'LL').startOf('day'))
  );
};

export const filterSeriesRecords = (source = [], query = DEFAULT_SERIES_QUERY, now = moment()) => {
  const { title, sponsor, type, time, active, site } = query || DEFAULT_SERIES_QUERY;
  const startDate = getSeriesStartDate(time, now);
  const searchText = normalize(title);
  const sponsorText = normalize(sponsor);

  return source
    .filter(rec => (searchText ? getSearchText(rec).includes(searchText) : true))
    .filter(rec => (sponsorText ? getSearchText(rec).includes(sponsorText) : true))
    .filter(rec =>
      isBlankSeriesFilter(time) || time === 'All Time'
        ? true
        : Boolean(rec?.data?.startDate) && moment(rec.data.startDate, 'LL').isSameOrAfter(startDate, 'day')
    )
    .filter(rec => (isBlankSeriesFilter(type) ? true : normalize(rec?.data?.type) === normalize(type)))
    .filter(rec => (active ? isSeriesActive(rec, now) : true))
    .filter(rec => (isBlankSeriesFilter(site) ? true : rec?.site_id === site));
};

export const SeriesSearchPage = ({
  getUsers,
  getSeries,
  getSites,
  sites,
  series,
  types,
  setSeriesQuery,
  seriesQuery,
  seriesLoading,
  seriesScope,
}) => {
  const [records, setRecords] = useState([]);

  const toRows = source =>
    _.chain(source || [])
      .map(record => toRow(record))
      .orderBy(['active', 'start'], ['asc', 'asc'])
      .value();

  const applyFilters = (query = seriesQuery, source = series) => {
    const results = filterSeriesRecords(source, query);
    setRecords(toRows(results));
  };

  useEffect(() => {
    applyFilters(seriesQuery, series);
  }, [series, seriesQuery]);

  useEffect(() => {
    if (seriesScope !== 'All') {
      getSeries('All');
    }
  }, [getSeries, seriesScope]);

  const dates = ['Today', 'Last 7 Days', 'Last 30 Days', 'This Year', 'All Time'];
  const dateOptions = buildOptionsFromStrings(dates);

  const handleSubmit = values => {
    const query = {
      ...seriesQuery,
      ...values,
      site: isBlankSeriesFilter(values?.site) ? 'All' : values.site,
      type: isBlankSeriesFilter(values?.type) ? 'All' : values.type,
      time: isBlankSeriesFilter(values?.time) ? 'All Time' : values.time,
    };

    setSeriesQuery(query);
    applyFilters(query);
  };

  const toTitleCase = str => {
    if (!str) return '';
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  const toRow = item => ({
    series: (
      <Link to={`/series/${item.site_id}/edit/${item.id}`}>{toTitleCase(item?.data?.headline || item?.title)}</Link>
    ),
    title: (
      <Link to={`/series/${item.site_id}/edit/${item.id}`}>
        {toTitleCase(item?.data?.name || item.title || item?.data?.title)}
      </Link>
    ),
    link: item.title,
    type: types.find(type => type.value === item?.data?.type)?.text,
    startDate: item?.data?.startDate ? moment(item?.data?.startDate, 'LL').format('LL') : '',
    start: item?.data?.startDate ? moment(item?.data?.startDate, 'LL').unix() : 0,
    endDate: item?.data?.endDate ? moment(item?.data?.endDate, 'LL').format('LL') : '',
    site: item.site,
    active: isSeriesActive(item),
  });

  const DATE_FORMAT = 'LL';
  const headers = [
    {
      Header: 'Series Name',
      accessor: 'series',
      sortType: (rowA, rowB) => {
        if (rowA.original['link'] > rowB.original['link']) return -1;
        if (rowB.original['link'] > rowA.original['link']) return 1;
        return 0;
      },
    },
    {
      Header: 'Internal Name',
      accessor: 'title',
      sortType: (rowA, rowB) => {
        if (rowA.original['link'] > rowB.original['link']) return -1;
        if (rowB.original['link'] > rowA.original['link']) return 1;
        return 0;
      },
    },
    { Header: 'Type', accessor: 'type' },
    {
      Header: 'Start Date',
      accessor: 'startDate',
      sortType: (rowA, rowB, id) => {
        const isMissingStartDate = pipe(path(['original', 'startDate']), isEmpty);
        if (isMissingStartDate(rowA) && !isMissingStartDate(rowB)) return -1;
        if (isMissingStartDate(rowB) && !isMissingStartDate(rowA)) return 1;
        if (moment(rowA.original[id], DATE_FORMAT).isBefore(moment(rowB.original[id], DATE_FORMAT))) return -1;
        if (moment(rowB.original[id], DATE_FORMAT).isBefore(moment(rowA.original[id], DATE_FORMAT))) return 1;
        return 0;
      },
    },
    {
      Header: 'End Date',
      accessor: 'endDate',
      sortType: (rowA, rowB, id) => {
        const isMissingStartDate = pipe(path(['original', 'startDate']), isEmpty);
        if (isMissingStartDate(rowA) && !isMissingStartDate(rowB)) return -1;
        if (isMissingStartDate(rowB) && !isMissingStartDate(rowA)) return 1;
        if (moment(rowA.original[id], DATE_FORMAT).isBefore(moment(rowB.original[id], DATE_FORMAT))) return -1;
        if (moment(rowB.original[id], DATE_FORMAT).isBefore(moment(rowA.original[id], DATE_FORMAT))) return 1;
        return 0;
      },
    },
    { Header: 'Site', accessor: 'site' },
  ];

  return (
    <PageContainer>
      <Formik initialValues={seriesQuery} enableReinitialize onSubmit={handleSubmit}>
        {formik => (
          <>
            <Breadcrumb>
              <Breadcrumb.Section>Series</Breadcrumb.Section>
              <Breadcrumb.Divider icon="right angle" />
              <Breadcrumb.Section>Dashboard</Breadcrumb.Section>
            </Breadcrumb>

            <Menu borderless size="tiny">
              <Menu.Item header>
                <Icon name="list" />
                Series
              </Menu.Item>

              <Menu.Menu position="right">
                <Menu.Item>
                  <AddSeriesModal />
                </Menu.Item>
              </Menu.Menu>
            </Menu>

            <div className="series-search-layout">
              <div>
                {seriesLoading && !records.length ? (
                  <Segment basic>
                    <Loader active inline="centered" content="Loading series..." />
                  </Segment>
                ) : (
                  <ReactTable sortColumn={''} sortDirection={'asc'} data={records} columns={headers} />
                )}
              </div>

              <Form size="tiny" widths="equal" onSubmit={formik.handleSubmit}>
                <Card>
                  <Card.Content>
                    <Form.Group>
                      <Form.Input
                        label="Search"
                        name="title"
                        formik={formik}
                        value={formik.values.title}
                        onChange={formik.handleChange}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Dropdown
                        name="site"
                        label="City"
                        clearable
                        className="time-dropdown"
                        value={formik.values.site}
                        onChange={(e, { value }) => formik.setFieldValue('site', value)}
                        fluid
                        selection
                        search
                        options={sites ? [{ key: 'All', value: 'All', text: 'All' }, ...sites.filter(s => s.key)] : []}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Dropdown
                        name="type"
                        label="Type"
                        clearable
                        className="time-dropdown"
                        value={formik.values.type}
                        onChange={(e, { value }) => formik.setFieldValue('type', value)}
                        fluid
                        selection
                        search
                        options={types}
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Checkbox
                        name="active"
                        label="Active"
                        checked={formik.values.active}
                        onChange={(e, { checked }) => formik.setFieldValue('active', checked)}
                        toggle
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Dropdown
                        selection
                        clearable
                        label="Publication Date"
                        className="time-dropdown"
                        value={formik.values.time}
                        onChange={(e, { value }) => {
                          formik.setFieldValue('time', value);
                          formik.setFieldValue('start_date', value);
                          formik.setFieldValue('end_date', value);
                        }}
                        fluid
                        search
                        options={dateOptions || []}
                      />
                    </Form.Group>

                    <Button
                      content="Search"
                      type="submit"
                      positive
                      fluid
                      size="tiny"
                    />
                    <br />
                    <Button
                      content="Clear Search"
                      fluid
                      type="button"
                      size="tiny"
                      onClick={() => {
                        setSeriesQuery(DEFAULT_SERIES_QUERY);
                        formik.resetForm({ values: DEFAULT_SERIES_QUERY });
                        applyFilters(DEFAULT_SERIES_QUERY);
                      }}
                    />
                    <br />
                    <Button
                      content="Refresh from RebelMouse"
                      fluid
                      type="button"
                      size="tiny"
                      loading={seriesLoading}
                      disabled={seriesLoading}
                      onClick={() => {
                        getSeries('All');
                      }}
                    />
                  </Card.Content>
                </Card>
              </Form>
            </div>
          </>
        )}
      </Formik>
    </PageContainer>
  );
};

const mapStateToProps = state => ({
  users: selectors.photos.userOptions(state),
  user: selectors.getUser(state),
  query: selectors.content.query(state),
  newsItems: selectors.content.newsItems(state),
  count: selectors.content.count(state),
  sites: selectors.getCultureMapSites(state),
  series: selectors.series.series(state),
  seriesLoading: selectors.series.loading(state),
  seriesScope: selectors.series.scope(state),
  types: selectors.series.types(state),
  seriesQuery: selectors.series.seriesQuery(state),
});

const mapDispatchToProps = dispatch => ({
  getSeries: bindActionCreators(actions.series.getSeries, dispatch),
  getUsers: bindActionCreators(actions.photos.getUsers, dispatch),
  setNewsItemQuery: bindActionCreators(actions.content.setNewsItemQuery, dispatch),
  setNewsItemPage: bindActionCreators(actions.content.setNewsItemPage, dispatch),
  getSites: bindActionCreators(actions.admin.getSites, dispatch),
  setSeriesQuery: bindActionCreators(actions.series.setSeriesQuery, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SeriesSearchPage);
